/**
 * FormDesigner — Maker + Holding Admin gateway page (v164).
 *
 * Lists every form_key the platform knows about (entities, founder
 * application, opportunities, meetings, …) along with the count of
 * system + custom fields per tenant. Click any row to drop into the
 * full editor for that form.
 *
 * Permissions:
 *   - system_admin  → may operate on any tenant via ?tenant=foo
 *   - holding_admin → operates on their own tenant only
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Settings2, Layers, Loader2, ArrowRight, Lock, Plus,
  Building2, Users, Sparkles, Handshake, Calendar, FileText, Briefcase,
} from 'lucide-react';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */

const CATEGORY_ICON = {
  Operations: Building2,
  People: Users,
  Pipeline: Sparkles,
  Legal: Handshake,
};

const FORM_ICON = {
  entity: Building2,
  user_profile: Users,
  founder_application: Sparkles,
  opportunity: Briefcase,
  meeting: Calendar,
  agreement: FileText,
  tenant_profile: Building2,
};

function FormCard({ form, onOpen }) {
  const Icon = FORM_ICON[form.form_key] || Layers;
  const hasCustom = form.custom_field_count > 0;
  return (
    <button
      type="button"
      data-testid={`form-card-${form.form_key}`}
      onClick={onOpen}
      className="text-left bg-white dark:bg-zinc-900/70 border border-slate-200 dark:border-white/10 rounded-xl p-5 hover:border-violet-500/60 dark:hover:border-violet-400/60 hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-300 border border-violet-500/20">
            <Icon size={18} />
          </span>
          <div>
            <div className="font-semibold text-slate-900 dark:text-white">
              {form.label}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-white/55 mt-0.5">
              {form.category}
            </div>
          </div>
        </div>
        <ArrowRight
          size={18}
          className="text-slate-400 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all mt-2"
        />
      </div>
      <p className="text-sm text-slate-600 dark:text-white/65 leading-relaxed mb-4">
        {form.description}
      </p>
      <div className="flex items-center gap-2 text-[11px] font-mono">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-white/65 border border-slate-200 dark:border-white/10"
          title="Locked system fields — cannot be deleted"
        >
          <Lock size={10} /> {form.system_field_count} system
        </span>
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border ${
            hasCustom
              ? 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30'
              : 'bg-slate-100 dark:bg-white/[0.03] text-slate-500 dark:text-white/45 border-slate-200 dark:border-white/10'
          }`}
        >
          <Plus size={10} /> {form.custom_field_count} custom
        </span>
      </div>
    </button>
  );
}

export default function FormDesigner() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const tenantParam = search.get('tenant') || null;

  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [tenantId, setTenantId] = useState(tenantParam || 'primary');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tenantParam ? { tenant_id: tenantParam } : {};
      const { data } = await api.get('/admin/forms', { params });
      setForms(data.forms || []);
      setTenantId(data.tenant_id);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to load forms';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [tenantParam]);

  useEffect(() => {
    load();
  }, [load]);

  // Group by category for clearer scan
  const byCategory = forms.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="form-designer-page">
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-lg">
            <Settings2 size={22} />
          </span>
          <div>
            <h1 className="text-3xl font-serif font-bold text-slate-900 dark:text-white">
              Form Designer
            </h1>
            <p className="text-sm text-slate-600 dark:text-white/65 mt-0.5">
              Customize the fields that appear on any form across your tenant.
              Tenant scope: <span className="font-mono text-violet-600 dark:text-violet-300">{tenantId}</span>
            </p>
          </div>
        </div>
        <div className="mt-3 rounded-md bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-700/40 text-amber-900 dark:text-amber-200 px-3 py-2 text-xs leading-relaxed flex items-start gap-2">
          <Lock size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>System fields are locked.</strong> They are required by the platform
            and cannot be renamed or deleted — but you can add any number of <strong>custom fields</strong> below
            them, mark them required or optional, and reorder them freely.
          </div>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 dark:text-white/55" data-testid="form-designer-loading">
          <Loader2 size={16} className="animate-spin" /> Loading forms…
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(byCategory).map(([cat, items]) => {
            const CatIcon = CATEGORY_ICON[cat] || Layers;
            return (
              <section key={cat} data-testid={`form-category-${cat.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55">
                  <CatIcon size={12} />
                  {cat}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {items.map((form) => (
                    <FormCard
                      key={form.form_key}
                      form={form}
                      onOpen={() => {
                        const q = tenantParam ? `?tenant=${encodeURIComponent(tenantParam)}` : '';
                        navigate(`/dashboard/admin/forms/${form.form_key}${q}`);
                      }}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
